import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Box, Button, Chip, Divider, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import { toast } from "react-toastify";
import { Autoplay } from "swiper";
import { setGlobalLoading } from "../../redux/features/globalLoadingSlice";
import uiConfigs from "../../configs/ui.configs";
import CircularRate from "./CircularRate";
import tmdbConfigs from "../../api/configs/tmdb.configs";
import genreApi from "../../api/modules/genre.api";
import mediaApi from "../../api/modules/media.api";
import mediaVideosApi from "../../api/modules/media.videos";

const HeroSlide = ({ mediaType, mediaCategory }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const [movies, setMovies] = useState([]);
  const [genres, setGenres] = useState([]);

  const handleTrailerPlay = async (movie) => {
    const { response, err } = await mediaVideosApi.getVideos({ 
      mediaType, 
      mediaId: movie.id 
    });

    if (err) {
      toast.error(err.message);
      return;
    }

    if (response?.results?.length > 0) {
      const trailer = response.results.find(video => video.type === "Trailer") || response.results[0];
      window.open(`https://www.youtube.com/watch?v=${trailer.key}`, '_blank');
    } else {
      toast.info("No trailer available");
    }
  };

  useEffect(() => {
    const getMedias = async () => {
      try {
        const { response, err } = await mediaApi.getList({
          mediaType,
          mediaCategory,
          page: 1
        });

        if (response) setMovies(response.results || []);
        if (err) toast.error(err.message);
        dispatch(setGlobalLoading(false));
      } catch (error) {
        toast.error("Failed to fetch media");
        setMovies([]);
      }
    };

    const getGenres = async () => {
      try {
        dispatch(setGlobalLoading(true));
        const { response, err } = await genreApi.getList({ mediaType });

        if (response) {
          setGenres(response.genres || []);
          getMedias();
        }
        if (err) {
          toast.error(err.message);
          setGlobalLoading(false);
        }
      } catch (error) {
        toast.error("Failed to fetch genres");
        setGenres([]);
      }
    };

    getGenres();
  }, [mediaType, mediaCategory, dispatch]);

  return (
    <Box sx={{
      position: "relative",
      color: "primary.contrastText",
      "&::before": {
        content: '""',
        width: "100%",
        height: "30%",
        position: "absolute",
        bottom: 0,
        left: 0,
        zIndex: 2,
        pointerEvents: "none",
        ...uiConfigs.style.gradientBgImage[theme.palette.mode]
      }
    }}>
      <Swiper
        grabCursor={true}
        loop={true}
        modules={[Autoplay]}
        style={{ width: "100%", height: "max-content" }}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false
        }}
      >
        {movies.map((movie, index) => (
          <SwiperSlide key={index}>
            <Box sx={{
              paddingTop: {
                xs: "130%",
                sm: "80%",
                md: "60%",
                lg: "45%"
              },
              backgroundPosition: "top",
              backgroundSize: "cover",
              backgroundImage: `url(${tmdbConfigs.backdropPath(movie.backdrop_path || movie.poster_path)})`,
              borderRadius: { xs: 0, md: "16px" },
              margin: { xs: 0, md: 2 },
              boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)"
            }} />
            <Box sx={{
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              ...uiConfigs.style.horizontalGradientBgImage[theme.palette.mode],
              borderRadius: { xs: 0, md: "16px" },
              margin: { xs: 0, md: 2 }
            }} />
            <Box sx={{
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              paddingX: { sm: "10px", md: "5rem", lg: "10rem" }
            }}>
              <Box sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                paddingX: "30px",
                color: "text.primary",
                width: { sm: "unset", md: "30%", lg: "40%" }
              }}>
                <Stack spacing={4} direction="column">
                  <Typography
                    variant="h4"
                    fontSize={{ xs: "2rem", md: "2rem", lg: "4rem" }}
                    fontWeight="700"
                    sx={{
                      ...uiConfigs.style.typoLines(2, "left"),
                      textShadow: "2px 2px 4px rgba(0,0,0,0.3)"
                    }}
                  >
                    {movie.title || movie.name}
                  </Typography>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularRate value={movie.vote_average} />
                    <Divider orientation="vertical" />
                    {movie.genre_ids && movie.genre_ids.length > 0 && genres.length > 0 && (
                      movie.genre_ids.slice(0, 2).map((genreId, index) => {
                        const genre = genres.find(g => g.id === genreId);
                        return genre ? (
                          <Chip
                            variant="filled"
                            color="secondary"
                            key={index}
                            label={genre.name}
                            sx={{ borderRadius: "16px" }}
                          />
                        ) : null;
                      })
                    )}
                  </Stack>

                  <Typography variant="body1" sx={{
                    ...uiConfigs.style.typoLines(3),
                    textShadow: "1px 1px 2px rgba(0,0,0,0.3)"
                  }}>
                    {movie.overview}
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => handleTrailerPlay(movie)}
                    sx={{ 
                      width: "max-content",
                      borderRadius: "24px",
                      textTransform: "none",
                      fontSize: "1.1rem",
                      padding: "8px 24px"
                    }}
                  >
                    Watch Trailer
                  </Button>
                </Stack>
              </Box>
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
};

export default HeroSlide;